export type ExternalServiceKind =
  | 'ai'
  | 'ozon'
  | 'google'
  | 'gateway'
  | 'payment'
  | 'sms'
  | 'logistics'
  | 'generic'

export type ExternalThinkingTask =
  | 'aiProcessing'
  | 'aiStrategy'
  | 'aiImage'
  | 'aiListing'
  | 'ozonSync'
  | 'ozonPublish'
  | 'ozonOrder'
  | 'gatewayCollect'
  | 'payment'
  | 'sms'
  | 'logistics'
  | 'generic'

export interface ExternalRequestFeedbackProfile {
  id: string
  kind: ExternalServiceKind
  task: ExternalThinkingTask
  label: string
  defaultMode?: 'panel' | 'overlay' | 'compact'
}

export interface ExternalRequestFeedbackEventDetail {
  requestId: string
  requestUrl: string
  method: string
  scopeId: string
  startedAt: number
  profile: ExternalRequestFeedbackProfile
}

export const EXTERNAL_REQUEST_FEEDBACK_START_EVENT = 'external-request-feedback:start'
export const EXTERNAL_REQUEST_FEEDBACK_END_EVENT = 'external-request-feedback:end'
export const EXTERNAL_REQUEST_SCOPE_ATTR = 'data-external-request-scope'
export const EXTERNAL_REQUEST_LONG_THRESHOLD_MS = 8000

const DEFAULT_EXTERNAL_PROFILE: ExternalRequestFeedbackProfile = {
  id: 'external-generic',
  kind: 'generic',
  task: 'generic',
  label: '外部服务',
  defaultMode: 'compact',
}

const normalizePath = (requestUrl: string): string => {
  try {
    return new URL(requestUrl, window.location.origin).pathname
  } catch {
    try {
      return new URL(requestUrl, 'https://yijianmai.local').pathname
    } catch {
      const [path] = String(requestUrl || '').split('?')
      return path || ''
    }
  }
}

const normalizeMethod = (init?: RequestInit): string => {
  return String(init?.method || 'GET').trim().toUpperCase() || 'GET'
}

const hasPathSegment = (path: string, segment: string): boolean => path.includes(segment)

export const classifyExternalRequest = (
  requestUrl: string,
  init?: RequestInit,
): ExternalRequestFeedbackProfile | null => {
  const path = normalizePath(requestUrl)
  const method = normalizeMethod(init)

  if (!path.startsWith('/api/')) {
    return null
  }

  if (
    method !== 'GET'
    && (
      /\/api\/v1\/collections\/[^/]+\/generate-stream$/.test(path)
      || /\/api\/v1\/collections\/[^/]+\/sku-recommendation$/.test(path)
    )
  ) {
    return { id: 'ai-generation', kind: 'ai', task: 'aiProcessing', label: 'AI 生成', defaultMode: 'panel' }
  }

  if (
    method !== 'GET'
    && (
      /\/api\/v1\/collections\/[^/]+\/image-slots\/generate$/.test(path)
      || /\/api\/v1\/collections\/[^/]+\/image-slots\/\d+\/regenerate$/.test(path)
      || /\/api\/v1\/variants\/tasks\/[^/]+\/items\/[^/]+\/regenerate-image$/.test(path)
    )
  ) {
    return { id: 'ai-image', kind: 'ai', task: 'aiImage', label: 'AI 图片生成', defaultMode: 'overlay' }
  }

  if (
    method !== 'GET'
    && (
      path === '/api/v1/variants/strategy-preview'
      || path === '/api/v1/variants/tasks'
      || /\/api\/v1\/variants\/tasks\/[^/]+\/retry-failed$/.test(path)
    )
  ) {
    return { id: 'ai-variant', kind: 'ai', task: 'aiStrategy', label: 'AI 变体策略', defaultMode: 'panel' }
  }

  if (
    method !== 'GET'
    && (
      path === '/api/v1/collect'
      || /\/api\/v1\/collect\/[^/]+\/retry$/.test(path)
      || path === '/api/v1/collect/plugin/broadcast'
    )
  ) {
    return { id: 'gateway-collect', kind: 'gateway', task: 'gatewayCollect', label: '采集中转服务', defaultMode: 'compact' }
  }

  if (
    method !== 'GET'
    && (
      path === '/api/v1/stores/bind/test'
      || path === '/api/v1/stores/bind'
      || /\/api\/v1\/stores\/[^/]+\/sync$/.test(path)
      || path === '/api/v1/store-catalog/sync'
      || path === '/api/v1/store-catalog/batch-jobs'
      || path === '/api/v1/pricing/logistics/providers/sync'
      || path === '/api/v1/collections/task-center/publish-status/refresh'
      || /\/api\/v1\/brands\/?$/.test(path)
    )
  ) {
    return { id: 'ozon-sync', kind: 'ozon', task: 'ozonSync', label: 'Ozon 接口', defaultMode: 'compact' }
  }

  if (/\/api\/v1\/brands\/search$/.test(path)) {
    return { id: 'ozon-brand-search', kind: 'ozon', task: 'ozonSync', label: 'Ozon 品牌查询', defaultMode: 'compact' }
  }

  if (
    method !== 'GET'
    && (
      /\/api\/v1\/collections\/[^/]+\/publish$/.test(path)
      || /\/api\/v1\/collections\/[^/]+\/publish\/[^/]+\/(relaunch|retry)$/.test(path)
      || /\/api\/v1\/collections\/task-center\/batches\/[^/]+\/publish/.test(path)
      || /\/api\/v1\/variants\/tasks\/[^/]+\/publish$/.test(path)
    )
  ) {
    return { id: 'ozon-publish', kind: 'ozon', task: 'ozonPublish', label: 'Ozon 发布', defaultMode: 'panel' }
  }

  if (
    method !== 'GET'
    && (
      /\/api\/v1\/orders\/[^/]+\/(prepare|ship)$/.test(path)
      || path === '/api/v1/orders/prepare/batch'
      || path === '/api/v1/orders/export/forwarder-sheet'
      || path === '/api/v1/orders/items/fulfillment/batch'
    )
  ) {
    return { id: 'ozon-order', kind: 'ozon', task: 'ozonOrder', label: 'Ozon 订单接口', defaultMode: 'compact' }
  }

  if (method !== 'GET' && (path === '/api/v1/auth/sms' || hasPathSegment(path, '/sms'))) {
    return { id: 'sms', kind: 'sms', task: 'sms', label: '短信服务', defaultMode: 'compact' }
  }

  if (
    path === '/api/v1/auth/turnstile'
    || hasPathSegment(path, 'turnstile')
    || hasPathSegment(path, 'captcha')
    || hasPathSegment(path, 'recaptcha')
  ) {
    return { id: 'gateway-turnstile', kind: 'gateway', task: 'gatewayCollect', label: '安全验证服务', defaultMode: 'compact' }
  }

  if (
    hasPathSegment(path, 'google')
    || hasPathSegment(path, 'gemini')
    || hasPathSegment(path, 'vertex')
  ) {
    return { id: 'google-service', kind: 'google', task: 'aiProcessing', label: 'Google 服务', defaultMode: 'compact' }
  }

  if (
    method !== 'GET'
    && (
      hasPathSegment(path, '/billing/pay')
      || hasPathSegment(path, '/billing/recharge')
      || hasPathSegment(path, '/billing/stripe')
      || hasPathSegment(path, '/payment/query')
      || hasPathSegment(path, '/subscription/portal')
    )
  ) {
    return { id: 'payment', kind: 'payment', task: 'payment', label: '支付服务', defaultMode: 'compact' }
  }

  if (method !== 'GET' && (hasPathSegment(path, 'logistics') || hasPathSegment(path, 'tracking'))) {
    return { id: 'logistics', kind: 'logistics', task: 'logistics', label: '物流服务', defaultMode: 'compact' }
  }

  return null
}

export const isExternalRequestProfile = (
  value: ExternalRequestFeedbackProfile | null | undefined,
): value is ExternalRequestFeedbackProfile => Boolean(value?.id && value.kind && value.task)

const AI_GENERAL_COPY = [
  'AI 正在加紧处理，这一步通常会多想几秒。',
  'AI 正在抓紧思考，正在把商品信息整理成可用结果。',
  'AI thinking · 正在理解当前任务上下文。',
  'AI 正在拆解商品信息，请稍等一下。',
  'AI 正在分析细节，不是系统卡住。',
  'AI 正在比对输入内容和历史结果。',
  'AI 正在推理下一步，外部模型返回可能需要一点时间。',
  'AI 正在组织答案，请不要重复点击。',
  'AI 正在确认关键字段，避免生成结果跑偏。',
  'AI 正在处理复杂内容，页面仍在正常工作。',
  'AI 正在集中注意力处理这个任务。',
  'AI 正在把零散信息整理成结构化结果。',
  'AI 正在计算更稳妥的方案。',
  'AI 正在检查上下文一致性。',
  'AI 正在生成候选结果并做内部筛选。',
  'AI 正在努力思考，外部服务还在返回中。',
  'AI 正在加速处理，请保持当前页面打开。',
  'AI 正在对齐商品、图片和发布要求。',
  'AI 正在处理外部模型请求，稍后会自动更新。',
  'AI 正在准备结果，这不是网络断开。',
]

const AI_STRATEGY_COPY = [
  'AI 正在判断 SKU 合并策略。',
  'AI 正在分析每个候选 SKU 的差异。',
  'AI 正在评估哪些变体适合合并。',
  'AI 正在比对颜色、规格和款式信息。',
  'AI 正在检查变体组合是否容易被平台拦截。',
  'AI 正在给每个 SKU 生成合并建议。',
  'AI 正在筛选高风险变体，请稍等。',
  'AI 正在推理变体创建顺序。',
  'AI 正在评估主 SKU 与候选 SKU 的关系。',
  'AI 正在整理变体策略摘要。',
  'AI 正在校验合并理由和置信度。',
  'AI 正在判断哪些图片需要重新生成。',
  'AI 正在避免把不该合并的 SKU 合到一起。',
  'AI 正在生成更稳妥的变体执行方案。',
  'AI 正在做最后的策略核对。',
]

const AI_IMAGE_COPY = [
  'AI 正在生成图片，请给外部模型一点时间。',
  'AI 正在理解参考图和商品卖点。',
  'AI 正在构图，避免图片偏离商品主体。',
  'AI 正在生成候选图像并等待回传。',
  'AI 正在处理图片细节，当前槽位会自动更新。',
  'AI 正在做图像生成，不是页面卡死。',
  'AI 正在对齐图片风格和平台规范。',
  'AI 正在生成更适合发布的主图。',
  'AI 正在检查图像内容是否可用。',
  'AI 正在等待图片服务返回结果。',
  'AI 正在补齐图片方案，请稍等。',
  'AI 正在重绘这张图，其他操作不受影响。',
  'AI 正在处理提示词和参考图。',
  'AI 正在优化画面细节。',
  'AI 图片服务响应较慢时，这里会持续等待。',
]

const AI_LISTING_COPY = [
  'AI 正在生成 Listing 文案。',
  'AI 正在整理标题、卖点和属性。',
  'AI 正在把采集信息转成平台可读内容。',
  'AI 正在优化商品表达。',
  'AI 正在处理多语言内容。',
  'AI 正在检查文案字段完整性。',
  'AI 正在生成更适合 Ozon 的描述。',
  'AI 正在补齐关键属性和关键词。',
  'AI 正在把图片、SKU 和文案对齐。',
  'AI 正在导出可发布内容。',
]

const OZON_SYNC_COPY = [
  '正在等待 Ozon 接口返回，平台响应慢时页面仍在处理。',
  'Ozon 接口正在处理中，请不要重复点击。',
  '正在与 Ozon 同步数据，外部平台可能需要几秒。',
  '正在连接 Ozon 服务，当前请求还在执行。',
  'Ozon 正在返回结果，请保持页面打开。',
  '正在校验 Ozon 店铺信息。',
  '正在同步 Ozon 商品数据。',
  '正在请求 Ozon 品牌/类目信息。',
  '正在等待 Ozon 后台确认。',
  'Ozon 平台偶尔会慢一点，我们会自动接收结果。',
  '正在把外部平台数据同步到系统。',
  '正在检查 Ozon 返回值是否完整。',
  '正在处理 Ozon API 响应。',
  '正在刷新 Ozon 最新状态。',
  '正在读取 Ozon 店铺配置。',
]

const OZON_PUBLISH_COPY = [
  '正在提交到 Ozon，平台审核返回可能需要一点时间。',
  '正在同步商品到 Ozon，请不要重复发布。',
  'Ozon 发布请求正在执行中。',
  '正在等待 Ozon 接收商品资料。',
  '正在把图片、价格和属性提交给 Ozon。',
  '正在等待 Ozon 返回发布结果。',
  'Ozon 平台正在处理这次发布。',
  '正在跟进发布状态，结果会自动更新。',
  '正在重新提交 Ozon 发布请求。',
  '正在核对 Ozon 返回的发布状态。',
  '正在处理平台审核反馈。',
  '正在同步发布记录和 Ozon 状态。',
  '正在等待外部平台确认，不是系统卡住。',
  '正在把本次修改推送到 Ozon。',
  '发布链路还在执行，请稍等。',
]

const OZON_ORDER_COPY = [
  '正在请求 Ozon 订单接口。',
  '正在等待 Ozon 返回订单操作结果。',
  '正在处理备货/发货请求，请不要重复点击。',
  'Ozon 订单接口响应中。',
  '正在生成或下载平台面单。',
  '正在同步订单履约状态。',
  '正在等待平台确认订单操作。',
  '正在处理 Ozon 发货链路。',
  '外部订单接口还在返回中。',
  '正在核对订单状态和可操作项。',
]

const GATEWAY_COPY = [
  '正在通过中转服务处理，请不要重复点击。',
  '中转服务正在转发请求，可能需要几秒。',
  '正在等待外部采集服务返回。',
  '正在连接采集通道，请保持页面打开。',
  '外部采集链路还在执行。',
  '正在提交采集任务到服务端队列。',
  '正在等待插件/中转站接收任务。',
  '请求正在外部通道里排队。',
  '正在处理站外页面数据。',
  '正在等待采集结果回传。',
  '中转链路响应慢时，这里会持续提示。',
  '正在和外部页面建立任务连接。',
  '正在确认采集任务是否已被接收。',
  '正在上传采集配置。',
  '外部服务还在返回中，不是系统卡住。',
]

const PAYMENT_COPY = [
  '正在等待支付服务返回。',
  '支付通道正在处理，请不要重复提交。',
  '正在确认支付状态。',
  '正在连接外部支付网关。',
  '正在生成支付或订阅请求。',
  '正在核对订单状态。',
  '支付服务可能需要几秒完成确认。',
  '正在等待第三方支付结果。',
  '正在同步充值状态。',
  '正在安全确认支付信息。',
]

const SMS_COPY = [
  '短信服务正在发送验证码。',
  '正在请求验证码，请稍等。',
  '短信通道正在处理。',
  '验证码发送可能需要几秒。',
  '正在等待短信服务返回。',
  '请不要重复点击发送验证码。',
  '正在校验发送频率。',
  '正在连接短信服务。',
  '验证码请求还在执行中。',
  '短信服务响应慢时，请稍等一下。',
]

const LOGISTICS_COPY = [
  '正在同步物流信息。',
  '正在请求物流服务返回。',
  '正在处理运单和履约数据。',
  '物流接口响应可能需要几秒。',
  '正在等待外部物流状态。',
  '正在更新国内物流跟踪。',
  '正在核对物流服务返回值。',
  '正在处理物流导出请求。',
  '正在等待承运商数据。',
  '物流链路还在执行中。',
]

const GENERIC_COPY = [
  '外部服务正在处理，请稍等。',
  '请求还在外部系统中执行。',
  '外部接口响应较慢时，这里会持续提示。',
  '正在等待第三方服务返回。',
  '系统没有卡住，只是在等外部服务。',
  '当前操作还在处理中。',
  '请保持页面打开，结果会自动更新。',
  '正在处理外部依赖请求。',
  '请求正在执行，请不要重复点击。',
  '稍等一下，外部服务还在返回中。',
]

export const EXTERNAL_THINKING_COPY_BY_TASK: Record<ExternalThinkingTask, string[]> = {
  aiProcessing: AI_GENERAL_COPY,
  aiStrategy: [...AI_STRATEGY_COPY, ...AI_GENERAL_COPY],
  aiImage: [...AI_IMAGE_COPY, ...AI_GENERAL_COPY],
  aiListing: [...AI_LISTING_COPY, ...AI_GENERAL_COPY],
  ozonSync: OZON_SYNC_COPY,
  ozonPublish: OZON_PUBLISH_COPY,
  ozonOrder: OZON_ORDER_COPY,
  gatewayCollect: GATEWAY_COPY,
  payment: PAYMENT_COPY,
  sms: SMS_COPY,
  logistics: LOGISTICS_COPY,
  generic: GENERIC_COPY,
}

const DEFAULT_TASK_BY_KIND: Record<ExternalServiceKind, ExternalThinkingTask> = {
  ai: 'aiProcessing',
  ozon: 'ozonSync',
  google: 'gatewayCollect',
  gateway: 'gatewayCollect',
  payment: 'payment',
  sms: 'sms',
  logistics: 'logistics',
  generic: 'generic',
}

export const getExternalThinkingCopyPool = (
  kind: ExternalServiceKind = DEFAULT_EXTERNAL_PROFILE.kind,
  task?: ExternalThinkingTask | null,
): string[] => {
  const resolvedTask = task || DEFAULT_TASK_BY_KIND[kind] || 'generic'
  const pool = EXTERNAL_THINKING_COPY_BY_TASK[resolvedTask] || EXTERNAL_THINKING_COPY_BY_TASK.generic
  return pool.length > 0 ? pool : EXTERNAL_THINKING_COPY_BY_TASK.generic
}

const hashString = (value: string): number => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0
  }
  return Math.abs(hash)
}

export const selectExternalThinkingCopy = (
  kind: ExternalServiceKind = DEFAULT_EXTERNAL_PROFILE.kind,
  task?: ExternalThinkingTask | null,
  seed = '',
): string => {
  const pool = getExternalThinkingCopyPool(kind, task)
  if (pool.length === 0) {
    return '外部服务正在处理，请稍等。'
  }
  const index = seed ? hashString(seed) % pool.length : Math.floor(Math.random() * pool.length)
  return pool[index] || pool[0]
}
